"use client";

import { useEffect, useState, useSyncExternalStore } from "react";
import type { ChatProcess, PlanBuildProcess, TestRunProcess } from "@/lib/chat/chatProcessTypes";
import { toneDot, toneText } from "@/lib/statusTone";
import type { WorkingStep } from "@/lib/chat/workingSteps";
import ReadMore from "../ReadMore";
import DotGlyph from "./DotGlyph";
import { elapsedLabel } from "./WorkingIndicator";

/** The server clock when the processes were read, paired with the local clock it was read at. */
export type ClockSync = { serverNow: number; localNow: number };

/** Steps shown in the stack; earlier ones are folded into Read more. */
export const VISIBLE_STEPS = 4;
/** After this long on one planning step, the clock says plans take a few minutes. */
export const LONG_STEP_S = 60;

function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? "" : "s"}`;
}

/** "Planner attempt 2 of 3, checks: 1 problem": the developer's line, under Read more. */
export function attemptLine(attempt: { seq: number; finishedAt: number | null; problems: number | null }, maxAttempts: number): string {
  const n = attempt.seq + 1;
  const name = n > maxAttempts ? `Planner attempt ${n} (extra)` : `Planner attempt ${n} of ${maxAttempts}`;
  if (attempt.finishedAt === null) return `${name}, waiting for the model…`;
  const problems = attempt.problems ?? 0;
  return `${name}, ${problems === 0 ? "checks passed" : `checks: ${plural(problems, "problem")}`}`;
}

/** Elapsed seconds since `since` by the server's clock, estimated from the last sync. */
function elapsedSince(since: number, clock: ClockSync | null, localNow: number): number {
  const serverNow = clock ? clock.serverNow + (localNow - clock.localNow) : localNow;
  return Math.max(0, Math.floor((serverNow - since) / 1000));
}

/**
 * One shared one-second clock for every timed step. Subscribing reads the
 * time at once, so the first render after a timed step appears is already
 * current; the last unsubscribe stops it.
 */
const ticker: { now: number; listeners: Set<() => void>; id: ReturnType<typeof setInterval> | null } = { now: 0, listeners: new Set(), id: null };
function subscribeTicker(listener: () => void): () => void {
  ticker.listeners.add(listener);
  if (ticker.id === null) {
    ticker.now = Date.now();
    ticker.id = setInterval(() => {
      ticker.now = Date.now();
      ticker.listeners.forEach((notify) => notify());
    }, 1000);
  }
  listener();
  return () => {
    ticker.listeners.delete(listener);
    if (ticker.listeners.size === 0 && ticker.id !== null) {
      clearInterval(ticker.id);
      ticker.id = null;
    }
  };
}
const subscribeNothing = () => () => {};
const readTicker = () => ticker.now;
const readNothing = () => 0;

/** Stepped trailing dots after the current step's words. Hidden from assistive technology. */
function TrailingDots() {
  const [n, setN] = useState(1);
  useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const id = setInterval(() => setN((v) => (v % 3) + 1), 420);
    return () => clearInterval(id);
  }, []);
  return <span aria-hidden="true" className="inline-block w-[3ch] text-zinc-400">{".".repeat(n)}</span>;
}

function StepPill({ step, clock, localNow }: { step: WorkingStep; clock: ClockSync | null; localNow: number }) {
  const current = step.state === "current";
  const glyphKind = step.state === "past" ? "done" : step.kind;
  const glyphTone = step.state === "failed" ? toneDot.danger
    : step.state === "past" || step.state === "done" ? toneDot.success
    : step.state === "next" ? toneDot.idle
    : toneDot.busy;
  const text = step.state === "done" ? toneText.success
    : step.state === "failed" ? toneText.danger
    : current ? "text-zinc-200"
    : "text-zinc-500";
  return (
    <li
      data-step={step.key}
      data-state={step.state}
      className={`caw-step-in inline-flex max-w-full items-center gap-2.5 rounded-[18px] border px-3 py-1.5 font-mono text-[12.5px] leading-snug ${text} ${
        current
          ? "border-white/[0.08] bg-zinc-800/55 shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_6px_18px_rgba(0,0,0,0.35)] backdrop-blur-md"
          : "border-white/[0.05] bg-zinc-900/50"
      }`}
    >
      <DotGlyph kind={glyphKind} progress={step.progress} animate={current} className={glyphTone} />
      {/* The words wrap; the dots and the clock stay together after the last word. */}
      <span className="flex min-w-0 flex-wrap items-baseline gap-x-1.5">
        <span className="min-w-0 break-words">{step.text}{current && <TrailingDots />}</span>
        {current && step.since !== undefined && (() => {
          const seconds = elapsedSince(step.since, clock, localNow);
          // A plan takes minutes; past one, say so, so a long draft does not look stuck.
          const slow = seconds >= LONG_STEP_S && (step.kind === "drafting" || step.kind === "retrying" || step.kind === "checking");
          return <span aria-hidden="true" className="whitespace-nowrap tabular-nums text-zinc-500">{elapsedLabel(seconds)}{slow ? " · plans take a few minutes" : ""}</span>;
        })()}
      </span>
    </li>
  );
}

function BuildDetail({ process }: { process: PlanBuildProcess }) {
  return (
    <>
      <div className="text-zinc-300">Plan for {process.workerName}</div>
      {process.attempts.map((attempt) => <div key={attempt.seq}>{attemptLine(attempt, process.maxAttempts)}</div>)}
      <div>Build {process.id}</div>
      {process.status !== "running" && process.status !== "built" && process.error && <div className={toneText.danger}>{process.error}</div>}
    </>
  );
}

function TestDetail({ process }: { process: TestRunProcess }) {
  return (
    <>
      <div className="text-zinc-300">Test of {process.workerName}</div>
      {process.steps.map((step, index) => (
        <div key={`${index}:${step.id}`}>{index + 1}. {step.label} ({step.id}) — {step.status === "success" ? "ok" : step.status === "error" ? "failed" : "unknown"}</div>
      ))}
      <div>Test run {process.id}</div>
      {process.revisionId && <div>Plan revision {process.revisionId}</div>}
      {process.coverage && <div>Coverage: {process.coverage.tested} of {process.coverage.total} steps</div>}
      {process.status === "error" && process.error && <div className={toneText.danger}>{process.error}</div>}
    </>
  );
}

/**
 * Smithers' work as a stack of steps: each stage a small pill, the current
 * one in glass with its dot glyph moving, finished ones dimmed with a tick.
 * Built from recorded data only (workingSteps); the technical detail of
 * every job sits under one Read more.
 * See docs/superpowers/specs/2026-10-19-working-steps-in-chat-design.md.
 */
export default function WorkingSteps({ steps, processes, clock }: { steps: WorkingStep[]; processes: ChatProcess[]; clock: ClockSync | null }) {
  const anyClock = steps.some((step) => step.state === "current" && step.since !== undefined);
  // Read from now the moment a timed step appears: the stack stays mounted between turns, so a kept time can be minutes old.
  const localNow = useSyncExternalStore(anyClock ? subscribeTicker : subscribeNothing, readTicker, readNothing);
  if (steps.length === 0) return null;
  const shown = steps.slice(-VISIBLE_STEPS);
  const folded = steps.slice(0, steps.length - shown.length);
  const label = ([...steps].reverse().find((step) => step.state === "current") ?? steps[steps.length - 1]).text;
  return (
    <div className="flex flex-col items-start gap-2">
      {/* Only the steps are the live region; Read more stays outside it, and pills are announced one by one. */}
      <ul role="status" aria-label={label} aria-atomic="false" className="flex max-w-[88%] flex-col items-start gap-1.5 sm:max-w-[78%]">
        {shown.map((step) => <StepPill key={step.key} step={step} clock={clock} localNow={localNow} />)}
      </ul>
      {(processes.length > 0 || folded.length > 0) && (
        <ReadMore className="pl-1">
          {folded.length > 0 && <div>Earlier: {folded.map((step) => step.text).join(" · ")}</div>}
          {processes.map((process) => (
            <div key={`${process.kind}:${process.id}`} className="space-y-0.5">
              {process.kind === "plan_build" ? <BuildDetail process={process} /> : <TestDetail process={process} />}
            </div>
          ))}
        </ReadMore>
      )}
    </div>
  );
}
